import {format} from 'date-fns';
import {combine, createEvent, forward, guard, merge, sample} from 'effector';

import i18n from 'i18next';
import {toast} from 'react-toastify';

import {RestError, showRestError} from 'core/errors';
import {initFiltersTable, initSortingsTable} from 'core/hooks/grid2.0';
import {ROUTE} from 'core/routes';
import {ApiFilterValue, SimplePaginationContent} from 'types/api';
import {InnerFilters} from 'types/grid2.0';
import {GtinsGridRawRow} from 'types/gtins';
import {mapFilters, mapSortings} from 'utils/grid-mappers';

import {
    data$,
    defaultFilters,
    defaultSortings,
    fetchDataFx,
    filters$,
    filtersChanged,
    filtersReset,
    Gate,
    pageNumber$,
    pageSize$,
    sortings$,
    sortingsChanged,
    sortingsReset,
    tableStateChanged,
    stateReset,
    getPackagingFormatTypeFx,
    packagingFormatTypeDictionary$,
    favoriteToggled,
    setFavoriteFx,
    favoritesSubmitted,
    setFavoritesFx,
    search$,
    searchChanged,
} from './index';

const updateFavorite = (
    state: SimplePaginationContent<GtinsGridRawRow> | null,
    gtin: string,
    status: boolean,
) => {
    if (!state) return state;

    const updatedContent = state.content
        .map((item) => (item.gtin === gtin ? {...item, isFavorite: status} : item))
        .filter((item) =>
            location.pathname === ROUTE.REGISTRIES_GTIN_FAVORITES ? item.isFavorite : true,
        );

    return {...state, content: updatedContent};
};

const updateFavorites = (
    state: SimplePaginationContent<GtinsGridRawRow> | null,
    gtins: string[],
    status: boolean,
) => {
    if (!state) return state;

    const gtinsSet = new Set(gtins);
    const updatedContent = status
        ? state.content.map((item) =>
              gtinsSet.has(item.gtin) ? {...item, isFavorite: true} : item,
          )
        : state.content.filter((item) => !gtinsSet.has(item.gtin));

    return {...state, content: updatedContent};
};

search$.on(searchChanged, (_, value) => value).reset(Gate.close);

data$
    .on(fetchDataFx.done, (_, {result}) => result)
    .reset(Gate.close)
    .on(favoriteToggled, (state, {gtin, currentStatus}) =>
        updateFavorite(state, gtin, !currentStatus),
    )
    .on(setFavoriteFx.done, (state, {params}) => updateFavorite(state, params.gtin, params.status))
    .on(setFavoriteFx.fail, (state, {params}) => updateFavorite(state, params.gtin, !params.status))
    .on(setFavoritesFx.done, (state, {params}) =>
        updateFavorites(state, params.gtins, params.status),
    );
pageNumber$.reset(filtersChanged, sortingsChanged, stateReset, searchChanged);
pageSize$.reset(stateReset);

initFiltersTable(filters$, filtersChanged, filtersReset, defaultFilters, stateReset);
initSortingsTable(sortings$, sortingsChanged, sortingsReset, defaultSortings, stateReset);

const mapFiltersHandler = (filters: InnerFilters) => {
    const mappedFilters = mapFilters(filters);
    const updatedAtFilter = mappedFilters.findIndex((filter) => filter.id === 'updatedAt');

    if (updatedAtFilter !== -1) {
        const updatedAtFilterValue = mappedFilters[updatedAtFilter];

        const dateValue = updatedAtFilterValue.value[0] as string;

        if (dateValue) {
            updatedAtFilterValue.value = (format(
                new Date(dateValue),
                'yyyy-MM-dd',
            ) as unknown) as ApiFilterValue;
        }
    }

    return mappedFilters;
};

sample({
    clock: merge([Gate.open, tableStateChanged, searchChanged]),
    source: combine({
        sortings: sortings$,
        pageNumber: pageNumber$,
        pageSize: pageSize$,
        filters: filters$,
        search: search$,
    }),
    fn: ({pageNumber, pageSize, filters, sortings, search}) => ({
        paging: {
            pageNumber,
            pageSize,
        },
        filters: mapFiltersHandler(filters),
        sortings: mapSortings(sortings),
        ...(search ? {gtinOrProduct: search} : {}),
    }),
    target: fetchDataFx,
});

sample({
    clock: favoriteToggled,
    fn: ({gtin, currentStatus}) => ({
        gtin,
        status: !currentStatus,
    }),
    target: setFavoriteFx,
});

sample({
    clock: favoritesSubmitted,
    target: setFavoritesFx,
});

const i18nLanguageChanged = createEvent<string>();

i18n.off('languageChanged', i18nLanguageChanged);
i18n.on('languageChanged', i18nLanguageChanged);

forward({
    from: Gate.open,
    to: getPackagingFormatTypeFx,
});

guard({
    source: i18nLanguageChanged,
    filter: Gate.status,
    target: getPackagingFormatTypeFx,
});

packagingFormatTypeDictionary$
    .on(getPackagingFormatTypeFx.doneData, (_, payload) => payload)
    .reset(Gate.close);

fetchDataFx.fail.watch(({error}) => showRestError(error as RestError));
setFavoriteFx.fail.watch(({error}) => showRestError(error as RestError));
setFavoritesFx.fail.watch(({error}) => showRestError(error as RestError));
setFavoritesFx.done.watch(({params}) => {
    toast.success(
        i18n.t(
            params.status
                ? 'pages.registries.gtins.favorites.addSuccess'
                : 'pages.registries.gtins.favorites.removeSuccess',
        ),
    );
});
